/**
 * FINN-side "Replace interviewer" on the Log — for when someone is out sick and the call still has
 * to happen, with the same time and the same candidate.
 *
 * Before this, swapping the interviewer meant cancelling and re-inviting: two candidate emails and a
 * spent booking link, for a change the candidate never needs to hear about. This keeps the booking
 * exactly where it is and changes only who takes it — the calendar invite moves to the new person and
 * the Lever panel follows. By product decision the candidate is NOT emailed.
 *
 * Scope: 1:1 interviews only (a panel is agreed against a whole group). The row only renders this
 * button for a 1:1 booking; the dialog and the proxy both refuse a panel as a second line of defence.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '../../../shared';
import {
  fetchReplacementInterviewers,
  replaceInterviewer,
  type ReplacementInterviewer,
} from '../services/interviewsApi';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import { ModalActions } from './taActionBoard/ModalActions';
import { PersonPicker } from './interviewLinks/PersonPicker';
import type { DirectoryPerson } from '../services/directoryApi';

const personLabel = (email?: string | null) =>
  !email
    ? '—'
    : email
        .split('@')[0]
        .split('.')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

/** The one-line warning for a candidate the calendars flag — advisory, never a block. */
function availabilityNote(c: ReplacementInterviewer): { text: string; tone: 'warn' | 'muted' } | null {
  if (c.available === true) return null;
  if (c.available === null) return { text: 'Could not check their calendar', tone: 'muted' };
  switch (c.reason) {
    case 'busy':
      return { text: 'Busy at this time, or at their interview limit for the day', tone: 'warn' };
    case 'outside_availability':
      return { text: 'Outside the hours they set for this link', tone: 'warn' };
    default:
      return { text: 'Not free at this time', tone: 'warn' };
  }
}

export function InterviewReplaceInterviewerButton({
  bookingRef,
  candidateName,
  onReplaced,
}: {
  bookingRef: string;
  candidateName: string | null;
  /** The list re-fetches, so the row shows the new interviewer from real server state. */
  onReplaced: () => void;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Replace interviewer…
      </Button>
    );
  }

  return (
    <Modal open onOpenChange={(next: boolean) => !next && setOpen(false)}>
      {/* Same overrides as the board's modals: below the DS md breakpoint ModalContent is a
          fullscreen sheet, which leaves a large empty band on a laptop. */}
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,34rem)] gap-5 overflow-auto rounded p-6">
        <ReplaceBody
          bookingRef={bookingRef}
          candidateName={candidateName}
          onClose={() => setOpen(false)}
          onReplaced={onReplaced}
        />
      </ModalContent>
    </Modal>
  );
}

/**
 * NOTE: deliberately NOT named "Modal…" — ModalContent clones any direct child whose name starts
 * with "Modal" to inject drawer props (see ActionModals).
 */
function ReplaceBody({
  bookingRef,
  candidateName,
  onClose,
  onReplaced,
}: {
  bookingRef: string;
  candidateName: string | null;
  onClose: () => void;
  onReplaced: () => void;
}) {
  const [selected, setSelected] = useState('');
  /** A colleague found through the search, who is not on the link. Takes this interview only. */
  const [outsider, setOutsider] = useState<DirectoryPerson | null>(null);
  /** Set once the server has refused a busy pick — the next confirm sends `override`. */
  const [clash, setClash] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data, isPending, error } = useQuery({
    queryKey: ['replacement-interviewers', bookingRef],
    queryFn: () => fetchReplacementInterviewers(bookingRef),
    staleTime: 60_000,
  });

  const poolCandidates = data?.candidates ?? [];
  // The searched colleague is listed after the link's interviewers, as one more choice. Their
  // calendar is only read when the server is asked, so no verdict is shown up front.
  const candidates: ReplacementInterviewer[] = outsider
    ? [
        ...poolCandidates,
        { email: outsider.email, name: outsider.name || '', available: null, reason: null },
      ]
    : poolCandidates;
  const isOutsider = (email: string) => outsider?.email === email;
  const selectedCandidate = candidates.find((c) => c.email === selected) ?? null;
  // We already know the pick is busy (the pool said so), so confirm straight away with override —
  // the inline warning IS the "warn and let them confirm" step. `clash` covers the race where the
  // calendar changed between the pool read and the click.
  const knownBusy = selectedCandidate?.available === false;

  const confirm = async () => {
    if (!selectedCandidate) return;
    setBusy(true);
    const override = knownBusy || clash;
    try {
      const result = await replaceInterviewer(bookingRef, { toEmail: selectedCandidate.email, override });
      if (result.clash) {
        // The calendars now say this person is busy — ask before sending.
        setClash(true);
        return;
      }
      if (!result.replaced && !result.demo) throw new Error('The interviewer was not changed — try again.');
      // Categories and booleans only — never the candidate, the interviewer or an email (PII rule).
      void logAnalyticsEvent('interview_interviewer_replaced', {
        overrode_busy: override,
        outside_link: isOutsider(selectedCandidate.email),
        calendar_result: result.calendar ?? 'unknown',
        lever_result: result.lever ?? 'unknown',
      });
      showToast({
        status: 'success',
        description: `${selectedCandidate.name || personLabel(selectedCandidate.email)} now takes ${
          candidateName || 'the candidate'
        }’s interview. The candidate was not emailed.`,
      });
      onReplaced();
      onClose();
    } catch (e) {
      void logAnalyticsEvent('interview_interviewer_replaced_failed', {});
      showToast({
        status: 'error',
        description: e instanceof Error ? e.message : 'Could not change the interviewer.',
      });
    } finally {
      setBusy(false);
    }
  };

  const panel = data && data.movable === false && data.meetingType === 'panel';
  const unlinked = data && data.movable === false && data.meetingType === null;

  return (
    <>
      <ModalHeader>
        <ModalTitle>Replace {candidateName || 'this candidate'}’s interviewer</ModalTitle>
      </ModalHeader>

      <p className="text-sm text-gray-600">
        The time stays the same and the candidate is not emailed. The new interviewer gets the
        calendar invite; the person being replaced is removed from it.
      </p>

      {isPending ? (
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking the interviewers’ calendars…
        </p>
      ) : error ? (
        <p className="text-sm text-red-700">Could not load the interviewer pool. Try again.</p>
      ) : panel ? (
        <p className="text-sm text-amber-800">
          This is a panel interview. Replacing one interviewer is only supported for 1:1 interviews —
          move or cancel it instead.
        </p>
      ) : unlinked ? (
        <p className="text-sm text-amber-800">
          {data?.slotError || 'The interview link no longer exists, so the interviewer cannot be changed here.'}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {data?.current ? (
            <p className="text-sm text-gray-700">
              Currently with{' '}
              <span className="font-medium text-gray-900">
                {data.current.name || personLabel(data.current.email)}
              </span>
              .
            </p>
          ) : null}

          {candidates.length === 0 ? (
            <p className="text-sm text-gray-700">
              No one else is on this link. Search for a colleague below.
            </p>
          ) : (
            <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Replacement interviewer">
              {candidates.map((c) => {
                const note = isOutsider(c.email)
                  ? { text: 'Not on this link — takes this interview only', tone: 'muted' as const }
                  : availabilityNote(c);
                const isSel = c.email === selected;
                return (
                  <button
                    key={c.email}
                    type="button"
                    role="radio"
                    aria-checked={isSel}
                    disabled={busy}
                    onClick={() => {
                      setSelected(c.email);
                      // A new pick starts the warn-and-confirm from scratch.
                      setClash(false);
                    }}
                    className={cn(
                      'flex flex-col items-start rounded-lg border px-3 py-2 text-left',
                      isSel ? 'border-gray-900 bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-400'
                    )}
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {c.name || personLabel(c.email)}
                    </span>
                    <span className="text-xs text-gray-500">{c.email}</span>
                    {note ? (
                      <span
                        className={cn(
                          'mt-1 inline-flex items-center gap-1 text-xs',
                          note.tone === 'warn' ? 'text-amber-800' : 'text-gray-500'
                        )}
                      >
                        {note.tone === 'warn' ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> : null}
                        {note.text}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}

          <PersonPicker
            label="Or pick any other colleague"
            placeholder="Search by name…"
            allowTypedEmail={false}
            excludeEmails={[
              ...(data?.current ? [data.current.email] : []),
              ...poolCandidates.map((c) => c.email),
            ]}
            onSelect={(person) => {
              setOutsider(person);
              setSelected(person.email);
              setClash(false);
            }}
          />
          <p className="text-xs text-gray-500">
            A colleague picked here takes this interview only. The link’s interviewer list stays as it
            is.
          </p>

          {clash ? (
            <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {selectedCandidate?.name || personLabel(selectedCandidate?.email)} is busy at this time.
                Confirm to use them anyway.
              </span>
            </p>
          ) : null}
        </div>
      )}

      <ModalActions
        confirmLabel={busy ? 'Replacing…' : clash ? 'Replace anyway' : 'Replace interviewer'}
        confirmDisabled={!selectedCandidate || busy}
        cancelLabel="Keep the interviewer"
        onConfirm={() => void confirm()}
        onCancel={onClose}
      />
    </>
  );
}
